import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from './use-toast';

export interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
  lastOnlineAt: Date | null;
  lastOfflineAt: Date | null;
}

/**
 * Represents a queued request that failed due to network issues
 */
export interface QueuedRequest {
  id: string;
  execute: () => Promise<unknown>;
  timestamp: Date;
  retryCount: number;
  maxRetries: number;
}

/**
 * Request queue for offline retry functionality
 */
class RequestQueue {
  private queue: QueuedRequest[] = [];
  private listeners: Set<(queue: QueuedRequest[]) => void> = new Set();
  private isProcessing = false;

  /**
   * Add a request to the queue
   */
  add(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount'>): string {
    const id = crypto.randomUUID();
    const queuedRequest: QueuedRequest = {
      ...request,
      id,
      timestamp: new Date(),
      retryCount: 0,
    };
    this.queue.push(queuedRequest);
    this.notifyListeners();
    return id;
  }

  /**
   * Remove a request from the queue
   */
  remove(id: string): boolean {
    const index = this.queue.findIndex((r) => r.id === id);
    if (index !== -1) {
      this.queue.splice(index, 1);
      this.notifyListeners();
      return true;
    }
    return false;
  }

  /**
   * Get all queued requests
   */
  getAll(): QueuedRequest[] {
    return [...this.queue];
  }

  /**
   * Get queue length
   */
  get length(): number {
    return this.queue.length;
  }

  /**
   * Clear all queued requests
   */
  clear(): void {
    this.queue = [];
    this.notifyListeners();
  }

  /**
   * Process all queued requests (replay them)
   */
  async processQueue(): Promise<{ succeeded: number; failed: number }> {
    if (this.isProcessing || this.queue.length === 0) {
      return { succeeded: 0, failed: 0 };
    }

    this.isProcessing = true;
    let succeeded = 0;
    let failed = 0;

    // Process queue in order (FIFO)
    const requestsToProcess = [...this.queue];
    this.queue = [];
    this.notifyListeners();

    for (const request of requestsToProcess) {
      try {
        await request.execute();
        succeeded++;
      } catch {
        // If request failed and has retries left, re-queue it
        if (request.retryCount < request.maxRetries) {
          this.queue.push({
            ...request,
            retryCount: request.retryCount + 1,
          });
        } else {
          failed++;
        }
      }
    }

    this.isProcessing = false;
    this.notifyListeners();
    return { succeeded, failed };
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: (queue: QueuedRequest[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const currentQueue = this.getAll();
    this.listeners.forEach((listener) => {
      listener(currentQueue);
    });
  }
}

// Singleton instance of the request queue
export const requestQueue = new RequestQueue();

export interface UseNetworkStatusOptions {
  /**
   * Whether to show toast notifications on status changes.
   * @default true
   */
  showToasts?: boolean;
  /**
   * Custom offline toast message.
   * @default "You're offline. Some features may not work."
   */
  offlineMessage?: string;
  /**
   * Custom online toast message.
   * @default "You're back online!"
   */
  onlineMessage?: string;
  /**
   * Whether to invalidate queries when coming back online.
   * @default true
   */
  invalidateQueriesOnReconnect?: boolean;
  /**
   * Whether to process queued requests when coming back online.
   * @default true
   */
  processQueueOnReconnect?: boolean;
  /**
   * Called when network goes offline.
   */
  onOffline?: () => void;
  /**
   * Called when network comes back online.
   */
  onOnline?: () => void;
}

export interface UseNetworkStatusReturn extends NetworkStatus {
  /**
   * Manually check and update the network status.
   */
  refresh: () => void;
  /**
   * Number of requests currently queued for retry.
   */
  queuedRequestCount: number;
  /**
   * Queue a request for retry when back online.
   * @param execute - Function that executes the request
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   * @returns The queued request ID
   */
  queueRequest: (execute: () => Promise<unknown>, maxRetries?: number) => string;
  /**
   * Remove a request from the queue.
   * @param id - The request ID to remove
   */
  removeQueuedRequest: (id: string) => void;
  /**
   * Clear all queued requests.
   */
  clearQueue: () => void;
}

/**
 * Hook for monitoring network connectivity status.
 *
 * Features:
 * - Detects online/offline state using navigator.onLine and events
 * - Shows toast notifications on status changes
 * - Integrates with TanStack Query to invalidate queries on reconnect
 * - Tracks history of offline periods
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isOnline, wasOffline } = useNetworkStatus();
 *
 *   return (
 *     <div>
 *       {!isOnline && <OfflineBanner />}
 *       {wasOffline && isOnline && <SyncingIndicator />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useNetworkStatus(options: UseNetworkStatusOptions = {}): UseNetworkStatusReturn {
  const {
    showToasts = true,
    offlineMessage = "You're offline. Some features may not work.",
    onlineMessage = "You're back online!",
    invalidateQueriesOnReconnect = true,
    processQueueOnReconnect = true,
    onOffline,
    onOnline,
  } = options;

  const queryClient = useQueryClient();
  const offlineToastRef = useRef<{ dismiss: () => void } | null>(null);
  const isInitialMount = useRef(true);

  const [status, setStatus] = useState<NetworkStatus>(() => ({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    wasOffline: false,
    lastOnlineAt: null,
    lastOfflineAt: null,
  }));

  const [queuedRequestCount, setQueuedRequestCount] = useState(requestQueue.length);

  const handleOnline = useCallback(async () => {
    const now = new Date();

    // Dismiss the offline toast if it's showing
    if (offlineToastRef.current) {
      offlineToastRef.current.dismiss();
      offlineToastRef.current = null;
    }

    setStatus((prev) => ({
      isOnline: true,
      wasOffline: true,
      lastOnlineAt: now,
      lastOfflineAt: prev.lastOfflineAt,
    }));

    // Don't show toast on initial mount
    if (!isInitialMount.current && showToasts) {
      toast.success({
        title: 'Connection Restored',
        description: onlineMessage,
        duration: 3000,
      });
    }

    // Invalidate all queries to refetch fresh data
    if (invalidateQueriesOnReconnect) {
      void queryClient.invalidateQueries();
    }

    // Process queued requests
    if (processQueueOnReconnect && requestQueue.length > 0) {
      const { succeeded, failed } = await requestQueue.processQueue();
      if (showToasts && (succeeded > 0 || failed > 0)) {
        if (failed === 0) {
          toast.success({
            title: 'Requests Synced',
            description: `${String(succeeded)} queued request${succeeded !== 1 ? 's' : ''} completed successfully.`,
            duration: 3000,
          });
        } else {
          toast.warning({
            title: 'Some Requests Failed',
            description: `${String(succeeded)} succeeded, ${String(failed)} failed after retries.`,
            duration: 5000,
          });
        }
      }
    }

    onOnline?.();
  }, [
    showToasts,
    onlineMessage,
    invalidateQueriesOnReconnect,
    processQueueOnReconnect,
    queryClient,
    onOnline,
  ]);

  const handleOffline = useCallback(() => {
    const now = new Date();

    setStatus((prev) => ({
      isOnline: false,
      wasOffline: prev.wasOffline || prev.isOnline, // was online at some point
      lastOnlineAt: prev.lastOnlineAt,
      lastOfflineAt: now,
    }));

    // Don't show toast on initial mount
    if (!isInitialMount.current && showToasts) {
      // Show a persistent offline toast (duration 0 = no auto-dismiss)
      const toastResult = toast.warning({
        title: 'No Internet Connection',
        description: offlineMessage,
        duration: 0, // Don't auto-dismiss
      });
      offlineToastRef.current = toastResult;
    }

    onOffline?.();
  }, [showToasts, offlineMessage, onOffline]);

  const refresh = useCallback(() => {
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (isOnline) {
      void handleOnline();
    } else {
      handleOffline();
    }
  }, [handleOnline, handleOffline]);

  const queueRequest = useCallback((execute: () => Promise<unknown>, maxRetries = 3): string => {
    return requestQueue.add({ execute, maxRetries });
  }, []);

  const removeQueuedRequest = useCallback((id: string): void => {
    requestQueue.remove(id);
  }, []);

  const clearQueue = useCallback((): void => {
    requestQueue.clear();
  }, []);

  // Subscribe to queue changes
  useEffect(() => {
    const unsubscribe = requestQueue.subscribe((queue) => {
      setQueuedRequestCount(queue.length);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // Mark initial mount as complete after first render
    isInitialMount.current = false;

    // Check initial state and show toast if offline
    if (!navigator.onLine && showToasts) {
      const toastResult = toast.warning({
        title: 'No Internet Connection',
        description: offlineMessage,
        duration: 0,
      });
      offlineToastRef.current = toastResult;
    }

    // Add event listeners - wrap async handler to avoid floating promise
    const onlineHandler = () => {
      void handleOnline();
    };
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', handleOffline);
      // Dismiss offline toast on unmount
      if (offlineToastRef.current) {
        offlineToastRef.current.dismiss();
      }
    };
  }, [handleOnline, handleOffline, showToasts, offlineMessage]);

  return {
    ...status,
    refresh,
    queuedRequestCount,
    queueRequest,
    removeQueuedRequest,
    clearQueue,
  };
}

export default useNetworkStatus;
